import * as React from 'react';

interface PendingStep {
	previousDueDate: string | null;
}

/**
 * Long enough that a normal save-then-refetch never hits it, short enough that a cell cannot sit
 * there spinning. Without it a refetch that errors would leave the spinner up permanently, and since
 * the spinner replaces the button the user could not even retry.
 */
export const PENDING_DUE_DATE_TIMEOUT_MS = 20_000;

/**
 * Tracks which step due dates are mid-update so their cells can show a spinner instead of a value
 * that is known to be wrong.
 *
 * A step stops being pending once its date differs from what it held at submit time, rather than
 * once it matches what was submitted — the server normalises the value it is given, so matching on
 * the submitted string would leave the spinner up forever.
 */
export function usePendingStepDueDates(steps: readonly { id?: string; dueDate?: string }[] | undefined) {
	const [pendingByStepStatusId, setPendingByStepStatusId] = React.useState<Record<string, PendingStep>>({});
	const timeoutsByStepStatusId = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});

	React.useEffect(() => {
		setPendingByStepStatusId(current => {
			const entries = Object.entries(current);
			if (entries.length === 0) {
				return current;
			}
			const stillPending = entries.filter(([stepStatusId, { previousDueDate }]) => {
				const step = steps?.find(x => x.id === stepStatusId);
				return !!step && (step.dueDate ?? null) === previousDueDate;
			});
			return stillPending.length === entries.length ? current : Object.fromEntries(stillPending);
		});
	}, [steps]);

	React.useEffect(() => {
		const timeouts = timeoutsByStepStatusId.current;
		return () => Object.values(timeouts).forEach(clearTimeout);
	}, []);

	const clearUpdating = React.useCallback((stepStatusId: string) => {
		clearTimeout(timeoutsByStepStatusId.current[stepStatusId]);
		delete timeoutsByStepStatusId.current[stepStatusId];
		setPendingByStepStatusId(current => {
			if (!current[stepStatusId]) {
				return current;
			}
			const next = { ...current };
			delete next[stepStatusId];
			return next;
		});
	}, []);

	const markUpdating = React.useCallback(
		(stepStatusId: string, previousDueDate?: string) => {
			clearTimeout(timeoutsByStepStatusId.current[stepStatusId]);
			timeoutsByStepStatusId.current[stepStatusId] = setTimeout(
				() => clearUpdating(stepStatusId),
				PENDING_DUE_DATE_TIMEOUT_MS
			);
			setPendingByStepStatusId(current => ({
				...current,
				[stepStatusId]: { previousDueDate: previousDueDate ?? null },
			}));
		},
		[clearUpdating]
	);

	return {
		clearUpdating,
		isUpdating: (stepStatusId?: string) => !!stepStatusId && !!pendingByStepStatusId[stepStatusId],
		markUpdating,
	};
}
